import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import './IvMpptstate.css';

const IvMpptstate = ({ SelInverter }) => {
  const Iv_list = useSelector((state) => state.Iv_list || {});
  const ivData = useSelector((state) => state.ivData || {});
  const [index, setIndex] = useState(null);
  const [protocol, setProtocol] = useState(null);

  useEffect(() => {
    if (Iv_list.seq && Array.isArray(Iv_list.seq)) {
      const foundIndex = Iv_list.seq.findIndex((seq) => seq === SelInverter);
      if (foundIndex !== -1) {
        setIndex(foundIndex);
        setProtocol(Iv_list.iv_protocol?.[foundIndex] || null);
      }
    }
  }, [Iv_list, SelInverter]);

  const PvCurrent = ivData?.PvCurrent?.[index] || [];
  const Mppt_Volt = ivData?.Mppt_Volt?.[index] || [];

  const getCurrentScale = (protocol) => {
    if (protocol === '350KHY') return 0.01;
    if (protocol === 'HY12N12' || protocol === 'HY12N24') return 0.1;
    return 1;
  };

  const renderCommonTable = (currentFixed = 1) => {
    const scale = getCurrentScale(protocol);
    const mpptNum = Iv_list.iv_number_of_mppt?.[index] ?? Mppt_Volt.length;
    const pvStringNum = Iv_list.iv_number_of_PVstrings_per_MPPT?.[index] ?? PvCurrent.length;
    const ivChNames = Iv_list.iv_pv_ch_name?.[index]?.split(',') || [];
    const chUsedArray = Iv_list.iv_pv_ch_use?.[index]?.split(',') || [];

    const numTables = Math.ceil(mpptNum / 4);
    const tables = [];

    for (let tableIndex = 0; tableIndex < numTables; tableIndex++) {
      const mpptStart = tableIndex * 4;
      const mpptEnd = Math.min(mpptStart + 4, mpptNum);
      const rows = [];

      for (let mppt = mpptStart; mppt < mpptEnd; mppt++) {
        const ch1 = mppt * 2;
        const ch2 = mppt * 2 + 1;

        const v = Mppt_Volt[mppt] ?? null;
        const voltage = v !== null ? (v / 10).toFixed(1) : '-';

        const c1 = chUsedArray[ch1] === '1' ? (PvCurrent[ch1] ?? 0) * scale : null;
        const c2 = chUsedArray[ch2] === '1' ? (PvCurrent[ch2] ?? 0) * scale : null;

        const power1 = voltage !== '-' && c1 !== null ? `${(parseFloat(voltage) * c1 / 1000).toFixed(3)} kW` : '-';
        const power2 = voltage !== '-' && c2 !== null ? `${(parseFloat(voltage) * c2 / 1000).toFixed(3)} kW` : '-';

        rows.push(
          <React.Fragment key={mppt}>
            <tr>
              <td rowSpan={2} className="mppt-highlight-cell">MPPT-{mppt + 1}</td>
              <td rowSpan={2}>{voltage !== '-' ? `${voltage} V` : '-'}</td>
              <td className="mppt-highlight-cell">{ivChNames[ch1] || `STR-${ch1 + 1}`}</td>
              <td>{c1 !== null ? `${c1.toFixed(currentFixed)} A` : '-'}</td>
              <td>{power1}</td>
            </tr>
            <tr>
              <td className="mppt-highlight-cell">{ivChNames[ch2] || `STR-${ch2 + 1}`}</td>
              <td>{c2 !== null ? `${c2.toFixed(currentFixed)} A` : '-'}</td>
              <td>{power2}</td>
            </tr>
          </React.Fragment>
        );
      }

      tables.push(
        <table key={tableIndex} className="mppt-table">
          <tbody>{rows}</tbody>
        </table>
      );
    }

    return <div className="mppt-state-wrapper-horizontal">{tables}</div>;
  };

  const renderHY12N12Table = () => {
    const scale = getCurrentScale(protocol);
    const mpptNum = Iv_list.iv_number_of_mppt?.[index] ?? Mppt_Volt.length;
    const chNames = Iv_list.iv_ch_name?.[index] || [];
    const chUsedArray = Iv_list.iv_pv_ch_use?.[index]?.split(',') || [];

    const numTables = Math.ceil(mpptNum / 5);
    const tables = [];

    for (let tableIndex = 0; tableIndex < numTables; tableIndex++) {
      const mpptStart = tableIndex * 5;
      const mpptEnd = Math.min(mpptStart + 5, mpptNum);
      const rows = [];

      for (let mppt = mpptStart; mppt < mpptEnd; mppt++) {
        const voltage = Mppt_Volt[mppt] !== undefined ? (Mppt_Volt[mppt] / 10).toFixed(1) : '-';
        const chUsed = chUsedArray[mppt] === '1';
        const current = chUsed && PvCurrent[mppt] !== undefined
          ? (PvCurrent[mppt] * scale).toFixed(1)
          : '-';
        const chName = chNames[mppt] || `STR-${mppt + 1}`;
        const power = voltage !== '-' && current !== '-'
          ? `${(parseFloat(voltage) * parseFloat(current) / 1000).toFixed(3)} kW`
          : '-';

        rows.push(
          <tr key={mppt}>
            <td className="mppt-highlight-cell">MPPT-{mppt + 1}</td>
            <td>{voltage !== '-' ? `${voltage} V` : '-'}</td>
            <td className="mppt-highlight-cell">{chName}</td>
            <td>{current !== '-' ? `${current} A` : '-'}</td>
            <td>{power}</td>
          </tr>
        );
      }

      tables.push(
        <table key={tableIndex} className="mppt-table">
          <tbody>{rows}</tbody>
        </table>
      );
    }

    return <div className="mppt-state-wrapper-horizontal">{tables}</div>;
  };

  const renderOCIPTable = () => {
    const scale = getCurrentScale(protocol);
    const mpptNum = Iv_list.iv_number_of_mppt?.[index] ?? Mppt_Volt.length;
    const pvStringNum = Iv_list.iv_number_of_PVstrings_per_MPPT?.[index] ?? PvCurrent.length;
    const ivChNames = Iv_list.iv_pv_ch_name?.[index]?.split(',') || [];
    const chUsedArray = Iv_list.iv_pv_ch_use?.[index]?.split(',') || [];

    const numTables = Math.ceil(mpptNum / 4);
    const tables = [];

    for (let tableIndex = 0; tableIndex < numTables; tableIndex++) {
      const mpptStart = tableIndex * 4;
      const mpptEnd = Math.min(mpptStart + 4, mpptNum);
      const rows = [];

      for (let mppt = mpptStart; mppt < mpptEnd; mppt++) {
        if (mppt >= 12 && mppt <= 14) continue;
        
        const ch1 = mppt * 2;
        const ch2 = mppt * 2 + 1;

        const v = Mppt_Volt[mppt] ?? null;
        const voltage = v !== null ? (v / 10).toFixed(1) : '-';

        const c1 = chUsedArray[ch1] === '1' ? (PvCurrent[ch1] ?? 0) * scale : null;
        const c2 = chUsedArray[ch2] === '1' ? (PvCurrent[ch2] ?? 0) * scale : null;

        const power1 = voltage !== '-' && c1 !== null ? `${(parseFloat(voltage) * c1 / 1000).toFixed(3)} kW` : '-';
        const power2 = voltage !== '-' && c2 !== null ? `${(parseFloat(voltage) * c2 / 1000).toFixed(3)} kW` : '-';

        rows.push(
          <React.Fragment key={mppt}>
            <tr>
              <td rowSpan={2} className="mppt-highlight-cell">MPPT-{mppt + 1}</td>
              <td rowSpan={2}>{voltage !== '-' ? `${voltage} V` : '-'}</td>
              <td className="mppt-highlight-cell">{ivChNames[ch1] || `STR-${ch1 + 1}`}</td>
              <td>{c1 !== null ? `${c1.toFixed(2)} A` : '-'}</td>
              <td>{power1}</td>
            </tr>
            <tr>
              <td className="mppt-highlight-cell">{ivChNames[ch2] || `STR-${ch2 + 1}`}</td>
              <td>{c2 !== null ? `${c2.toFixed(2)} A` : '-'}</td>
              <td>{power2}</td>
            </tr>
          </React.Fragment>
        );
      }

      if (rows.length > 0) {
        tables.push(
          <table key={tableIndex} className="mppt-table">
            <tbody>{rows}</tbody>
          </table>
        );
      }
    }

    return <div className="mppt-state-wrapper-horizontal">{tables}</div>;
  };



  return (
    <>
      {(protocol === 'HY12N24' || protocol === '350KHY') &&
        renderCommonTable(protocol === '350KHY' ? 2 : 1)}

      {protocol === 'HY12N12' && renderHY12N12Table()}

      {protocol === 'ocip' && renderOCIPTable()}

      {protocol === 'growatt' && renderOCIPTable()}

      {!['HY12N24', '350KHY', 'HY12N12', 'ocip', 'growatt'].includes(protocol) && (
        <div className="mppt-state-wrapper-horizontal">
          <p>아직 이 프로토콜({protocol})에 대한 테이블이 구현되지 않았습니다.</p>
        </div>
      )}
    </>
  );
};

export default IvMpptstate;
